import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { getJointPositionActuatorSliderState } from "../../physics/jointLabelActuatorDrag";
import { hasPhysicsWorker } from "../../physics/workerHandle";
import { usePhysicsStore } from "../../stores/physicsStore";
import {
  useActuators,
  useCameras,
  useJoints,
  useLights,
  useMaterials,
  useMeshes,
  useSceneStore,
  useSites,
} from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { useMeshFlatShading, useMeshOpacity, useViewerStore } from "../../stores/viewerStore";
import { usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import { useSelectedVisualMeshIds } from "../../visual-mesh-multi-selection";
import { indexActuatorsForJointVisualization } from "../jointActuatorLabelUtils";
import { EMPTY_OBJECT } from "../scene/sceneConstants";
import { equalJointActuatorSliderSnapshot } from "../scene/sceneMisc";

export function useSceneViewportState() {
  const selection = useSelectionStore(
    useShallow((state) => ({
      selectedBody: state.selectedBody,
      lastSelectionSource: state.lastSelectionSource,
      selectedMesh: state.selectedMesh,
      selectedJoint: state.selectedJoint,
      selectedSite: state.selectedSite,
      selectedGeom: state.selectedGeom,
      selectedInertial: state.selectedInertial,
      selectedEquality: state.selectedEquality,
      selectedTendon: state.selectedTendon,
      hoveredBody: state.hoveredBody,
      hoveredMesh: state.hoveredMesh,
      hoveredJoint: state.hoveredJoint,
      focusModeActive: state.focusModeActive,
      focusBodyName: state.focusBodyName,
      focusMeshUuid: state.focusMeshUuid,
      focusCollisionGeomUuid: state.focusCollisionGeomUuid,
    }))
  );
  const selectionActions = useSelectionStore.getState();
  const selectedBodiesMultiKey = useSelectionStore((state) =>
    state.selectedBodies.length > 1 ? [...state.selectedBodies].sort().join("\x1e") : ""
  );
  const selectedVisualMeshIdsForScene = useSelectedVisualMeshIds();
  const selectedVisualMeshMultiKey =
    selectedVisualMeshIdsForScene.size > 1
      ? Array.from(selectedVisualMeshIdsForScene).sort().join("\x1e")
      : "";

  const viewer = useViewerStore(
    useShallow((state) => ({
      useYUp: state.useYUp,
      showCollision: state.showCollision,
      showSites: state.showSites,
      showVisual: state.showVisual,
      showJoints: state.showJoints,
      showInertials: state.showInertials,
      showGrasps: state.showGrasps,
      showPositionGizmo: state.showPositionGizmo,
      jointVizVisibility: state.jointVizVisibility,
      meshVisibility: state.meshVisibility,
      bodyVisibility: state.bodyVisibility,
      siteVisibility: state.siteVisibility,
      collisionGeomVisibility: state.collisionGeomVisibility,
      inertialVisibility: state.inertialVisibility,
      equalityMarkerVisibility: state.equalityMarkerVisibility,
      tendonMarkerVisibility: state.tendonMarkerVisibility,
      cameraMarkerVisibility: state.cameraMarkerVisibility,
      collisionGeomColor: state.collisionGeomColor,
      collisionGeomColorMode: state.collisionGeomColorMode,
      collisionGeomColorsByKind: state.collisionGeomColorsByKind,
      siteVizColor: state.siteVizColor,
      siteVizSelectedColor: state.siteVizSelectedColor,
      showConstraintMarkers: state.showConstraintMarkers,
      showActuators: state.showActuators,
    }))
  );

  const isPhysicsRunning = usePhysicsAuthoringLock();
  const actuatorSliderState = useStoreWithEqualityFn(
    usePhysicsStore,
    (s) => {
      if (!s.isRunning || !hasPhysicsWorker() || !selection.selectedJoint) return null;
      return getJointPositionActuatorSliderState(
        selection.selectedJoint,
        s.actuatorPositionTargets
      );
    },
    equalJointActuatorSliderSnapshot
  );

  const materials = useMaterials();
  const meshOpacity = useMeshOpacity();
  const meshFlatShading = useMeshFlatShading();
  const safeJointMapObject = useJoints();
  const allEqualities = useSceneStore((s) => s.equality);
  const allTendons = useSceneStore((s) => s.tendons);
  const actuators = useActuators();
  const savedCameras = useCameras();
  const savedLights = useLights();
  const allMeshes = useMeshes();
  const allSites = useSites();
  const actuatorSignature = useMemo(
    () =>
      Object.values(actuators || EMPTY_OBJECT)
        .map(
          (actuator) =>
            `${actuator?.uuid || actuator?.name || ""}:${actuator?.joint || ""}:${actuator?.tendonUuid || ""}`
        )
        .sort()
        .join(","),
    [actuators]
  );
  const jointEntries = useMemo(() => Object.values(safeJointMapObject), [safeJointMapObject]);
  const { actuatedJointUuids, actuatorDisplayLabelByJointUuid } = useMemo(
    () =>
      indexActuatorsForJointVisualization(
        actuators || EMPTY_OBJECT,
        safeJointMapObject || EMPTY_OBJECT
      ),
    [actuators, safeJointMapObject]
  );
  const jointKindsSignature = useMemo(
    () =>
      Object.entries(safeJointMapObject)
        .map(([k, v]) => `${k}:${v?.kind ?? ""}`)
        .sort()
        .join(","),
    [safeJointMapObject]
  );
  const jointLookup = useMemo(() => {
    const map = new Map();
    jointEntries.forEach((joint) => {
      if (!joint?.uuid) return;
      const range = joint.range ?? [-Math.PI, Math.PI];
      let axisArray;
      if (Array.isArray(joint.axis) && joint.axis.length === 3) {
        axisArray = joint.axis;
      } else if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(joint.axis)) {
        axisArray = Array.from(joint.axis).slice(0, 3);
      } else {
        axisArray = [0, 0, 1];
      }
      const entry = {
        joint,
        current: joint.kind === "ball" ? (joint.quat ?? 0) : (joint.ref ?? 0),
        min: range[0],
        max: range[1],
        axis: axisArray,
        useLimits: joint.useLimits ?? "auto",
      };
      map.set(joint.uuid, entry);
    });
    return map;
  }, [jointEntries]);

  return {
    ...selection,
    ...viewer,
    actuatorDisplayLabelByJointUuid,
    actuatorSignature,
    actuatorSliderState,
    actuators,
    actuatedJointUuids,
    allEqualities,
    allMeshes,
    allSites,
    allTendons,
    isPhysicsRunning,
    jointEntries,
    jointKindsSignature,
    jointLookup,
    materials,
    meshFlatShading,
    meshOpacity,
    safeJointMapObject,
    savedCameras,
    savedLights,
    selectedBodiesMultiKey,
    selectedVisualMeshIdsForScene,
    selectedVisualMeshMultiKey,
    selectionActions,
  };
}
